<template>
  <div class="vehicle-list">
    <v-row dense class="mb-4" align="center">
      <v-col cols="auto" style="min-width: 260px">
        <v-text-field
            :label="$t('search')"
            clearable
            outlined
            persistent-placeholder
            v-model="searchText"
            hide-details
            @keyup.enter="search"
        />
      </v-col>
      <v-col cols="auto" style="min-width: 220px">
        <v-select
            v-model="connectionState"
            :items="connectionStateOptions"
            :label="$t('vehicle.connection-state')"
            clearable
            outlined
            hide-details
            dense
            @change="search"
        >
          <template #item="{ item }">
            <v-icon small :color="item.color" class="mr-2">{{ item.icon }}</v-icon>
            {{ item.text }}
          </template>
        </v-select>
      </v-col>
      <v-col cols="auto">
        <v-checkbox
            v-model="showArchived"
            :label="$t('vehicle.show-archived')"
            hide-details
            class="mt-0"
            @change="search"
        />
      </v-col>
      <v-col cols="auto">
        <v-checkbox
            v-model="needsAction"
            :label="$t('vehicle.connection-action-required')"
            hide-details
            class="mt-0"
            color="error"
            @change="search"
        />
      </v-col>
      <v-col cols="auto">
        <v-btn small @click="search">
          {{ $t('search') }}
        </v-btn>
      </v-col>
      <v-spacer />
      <v-col cols="auto" v-if="currentUser.permissions.includes('VEHICLE_WRITE')">
        <v-btn color="primary" small @click="showAddDialog = true">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('vehicle.add-vehicle') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
        :headers="tableHeaders"
        :items="items"
        :options.sync="options"
        :server-items-length="total"
        class="row-pointer"
    >
      <template #item.registrationNumber="{item, value}">
        <router-link class="click" :to="{ name: 'vehicle.detail', params: { id: item.id } }" @click.native.stop>
          {{ value || '-' }}
        </router-link>
      </template>

      <template #item.customer.lastname="{item}">
        <template v-if="item.customer">
          {{ item.customer | customerLabel }}
        </template>
        <template v-else>-</template>
      </template>

      <template #item.organization.name="{item}">
        <template v-if="item.organization">
          {{ item.organization.name }}
        </template>
        <template v-else>-</template>
      </template>

      <template #item.brandModel="{item}">
        <template v-if="item.version && item.version.brand">
          {{ item.version.brand }} {{ item.version.model || '' }}
        </template>
        <template v-else>-</template>
      </template>

      <template #item.connectionState="{item}">
        <v-chip x-small :color="connectionMeta(item.connectionState).chipColor" :text-color="connectionMeta(item.connectionState).textColor" label>
          <v-icon x-small left>{{ connectionMeta(item.connectionState).icon }}</v-icon>
          {{ connectionMeta(item.connectionState).text }}
        </v-chip>
        <!-- Connection HEALTH (Lot 3): an "action required" badge when the connection needs re-auth/reconnect. -->
        <v-tooltip bottom v-if="item.connectionHealthRequiresAction">
          <template #activator="{ on, attrs }">
            <v-chip x-small color="error" dark label class="ml-1" v-on="on" v-bind="attrs">
              <v-icon x-small left>mdi-alert</v-icon>{{ $t('vehicle.connection-action-required') }}
            </v-chip>
          </template>
          <span>{{ $t('vehicle.telematics-health.' + (item.connectionHealth === 'NEEDS_RECONNECT' ? 'reconnect' : 'reauth') + '-title') }}</span>
        </v-tooltip>
      </template>

      <template #item.createdAt="{item}">
        {{ formatDate(item.createdAt, $t('format_datetime')) }}
      </template>

      <template #item.archived="{item}">
        <v-chip v-if="item.archived" x-small color="grey" text-color="white">
          {{ $t('vehicle.archived') }}
        </v-chip>
      </template>

      <template #item.actions="{item}">
        <div class="d-flex align-center justify-end">
          <v-btn v-if="canTransfer" icon small @click.stop="openTransferDialog(item)" :title="$t('vehicle.transfer-vehicle')">
            <v-icon small>mdi-swap-horizontal</v-icon>
          </v-btn>
          <v-menu v-if="canWrite && connectionActions(item).length" offset-y left>
            <template #activator="{ on, attrs }">
              <v-btn icon small v-on="on" v-bind="attrs" @click.stop :title="$t('vehicle.telematics-manage')">
                <v-icon small>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item v-for="action in connectionActions(item)" :key="action.key" @click.stop="action.handler(item)">
                <v-list-item-icon class="mr-2"><v-icon small :color="action.color">{{ action.icon }}</v-icon></v-list-item-icon>
                <v-list-item-title :class="action.color === 'error' ? 'error--text' : ''">{{ action.text }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </template>
    </v-data-table>

    <v-btn v-if="exportable"  :loading="exporting" @click="exportCsv" class="mt-4">
      <v-icon left>mdi-file-excel</v-icon>
      {{ $t('export') }}
    </v-btn>

    <vehicle-wizard-dialog
        v-model="showAddDialog"
        :organization-id="organizationId"
        :organization-name="organizationName"
        :customer-id="customerId"
        :customer-name="customerName"
        :member-id="memberId"
        @vehicle-added="loadData"
    />

    <vehicle-transfer-dialog
        v-if="organizationId"
        v-model="showTransferDialog"
        :vehicle="transferVehicle"
        :organization-id="organizationId"
        @vehicle-transferred="loadData"
    />
  </div>
</template>

<script>
import VehicleRepository from "@repository/VehicleRepository";
import VehicleWizardDialog from "@/components/pages/Vehicle/modals/VehicleWizardDialog.vue";
import VehicleTransferDialog from "@/components/pages/Vehicle/modals/VehicleTransferDialog.vue";

export default {
  components: { VehicleWizardDialog, VehicleTransferDialog },

  props: {
    organizationId: String,
    organizationName: String,
    customerId: String,
    customerName: String,
    memberId: String,
    exportable: Boolean,
  },

  data() {
    return {
      items: [],
      total: 0,
      searchText: null,
      showArchived: false,
      connectionState: null,
      needsAction: false,
      options: {
        sortBy: ['createdAt'],
        sortDesc: [true],
      },
      showAddDialog: false,
      showTransferDialog: false,
      transferVehicle: null,
      exporting: false,
    }
  },

  watch: {
    options() {
      this.loadData()
    },
  },

  computed: {
    tableHeaders() {
      let headers = [
        { text: this.$t('vehicle.registration-number'), value: 'registrationNumber' },
        { text: this.$t('vehicle.name'), value: 'name' },
        { text: this.$t('vehicle.brand') + ' / ' + this.$t('vehicle.model'), value: 'brandModel', sortable: false },
      ]

      if (!this.customerId) {
        headers.push({ text: this.$tc('customer.customer'), value: 'customer.lastname', sortable: false })
      }

      if (!this.organizationId) {
        headers.push({ text: this.$t('organization.organization'), value: 'organization.name', sortable: false })
      }

      headers.push({ text: this.$t('vehicle.connection-state'), value: 'connectionState' })
      headers.push({ text: this.$t('created-at'), value: 'createdAt' })

      if (this.showArchived) {
        headers.push({ text: '', value: 'archived', sortable: false })
      }

      if (this.canTransfer || this.canWrite) {
        headers.push({ text: '', value: 'actions', sortable: false, width: '96px' })
      }

      return headers
    },

    canWrite() {
      return this.currentUser.permissions.includes('VEHICLE_WRITE')
    },

    canTransfer() {
      return this.canWrite && !!this.organizationId
    },

    // Options for the connection-state filter (chip-decorated in the dropdown).
    connectionStateOptions() {
      return ['not_configured', 'awaiting_consent', 'connected'].map(value => ({
        value,
        text: this.connectionMeta(value).text,
        icon: this.connectionMeta(value).icon,
        color: this.connectionMeta(value).chipColor,
      }))
    },
  },

  methods: {
    search() {
      if (this.options.page > 1) {
        this.options.page = 1
      } else {
        this.loadData()
      }
    },

    loadData() {
      let data = {
        searchText: this.searchText,
        showArchived: this.showArchived,
      }

      if (this.connectionState) data.connectionState = this.connectionState
      if (this.needsAction) data.needsAction = true
      if (this.organizationId) data.organizationId = this.organizationId
      if (this.customerId) data.customerId = this.customerId

      if (this.options.itemsPerPage > -1) {
        data.offset = (this.options.page * this.options.itemsPerPage) - this.options.itemsPerPage
      }
      data.limit = this.options.itemsPerPage

      if (this.options.sortBy.length > 0) {
        data.orderBy = this.options.sortBy[0]
      }
      if (this.options.sortDesc.length > 0) {
        data.orderDirection = this.options.sortDesc[0] ? 'desc' : 'asc'
      }

      this.showLoading(true)
      VehicleRepository.search(data).then(response => {
        this.items = response.items
        this.total = response.total
        this.showLoading(false)
      }).catch(err => {
        if (err.message !== 'canceled') {
          this.notifyError(err)
          this.showLoading(false)
        }
      })
    },

    goToDetail(item) {
      this.$router.push({ name: 'vehicle.detail', params: { id: item.id } })
    },

    exportCsv() {
      let data = {
        searchText: this.searchText,
        showArchived: this.showArchived,
      }

      if (this.organizationId) data.organizationId = this.organizationId
      if (this.customerId) data.customerId = this.customerId

      if (this.options.sortBy.length > 0) {
        data.orderBy = this.options.sortBy[0]
      }
      if (this.options.sortDesc.length > 0) {
        data.orderDirection = this.options.sortDesc[0] ? 'desc' : 'asc'
      }

      this.exporting = true
      VehicleRepository.exportCsv(data).then(() => {
        this.exporting = false
      }).catch(err => {
        this.exporting = false
        this.notifyError(err)
      })
    },

    openTransferDialog(item) {
      this.transferVehicle = item
      this.showTransferDialog = true
    },

    // Presentation for a connection state (null legacy rows = not configured).
    connectionMeta(state) {
      const s = state || 'not_configured'
      const map = {
        not_configured: { icon: 'mdi-circle-outline', chipColor: 'grey lighten-3', textColor: 'grey darken-1' },
        awaiting_consent: { icon: 'mdi-clock-outline', chipColor: 'orange lighten-4', textColor: 'orange darken-3' },
        connected: { icon: 'mdi-check-circle', chipColor: 'green lighten-5', textColor: 'green darken-2' },
      }
      return { ...(map[s] || map.not_configured), text: this.$t('vehicle.connection-state-' + s) }
    },

    // Row actions available for the current connection state.
    connectionActions(item) {
      const state = item.connectionState || 'not_configured'
      const actions = []
      if (state === 'awaiting_consent') {
        actions.push({ key: 'resend', text: this.$t('vehicle.telematics-resend-consent'), icon: 'mdi-email-fast-outline', color: 'primary', handler: this.resendConsent })
      }
      if (state !== 'not_configured') {
        actions.push({ key: 'edit', text: this.$t('vehicle.telematics-change-connection'), icon: 'mdi-pencil-outline', handler: this.editConnection })
        actions.push({ key: 'terminate', text: this.$t('vehicle.telematics-terminate'), icon: 'mdi-link-off', color: 'error', handler: this.terminateConnection })
      }
      return actions
    },

    resendConsent(item) {
      this.showLoading(true)
      VehicleRepository.resendTelematicsConsent(item.id).then(() => {
        this.showLoading(false)
        this.$dialog.notify.success(this.$t('vehicle.consent-link-sent'))
      }).catch(err => {
        this.showLoading(false)
        this.notifyError(err)
      })
    },

    // Editing the config means the wizard, which lives on the vehicle's Connection tab → deep-link there.
    editConnection(item) {
      this.$router.push({ name: 'vehicle.telematics', params: { id: item.id } })
    },

    terminateConnection(item) {
      this.$dialog.confirm({ text: this.$t('vehicle.telematics-confirm-terminate') }).then(result => {
        if (!result) return
        this.showLoading(true)
        VehicleRepository.terminateTelematicsConnection(item.id).then(() => {
          this.showLoading(false)
          this.loadData()
        }).catch(err => {
          this.showLoading(false)
          this.notifyError(err)
        })
      })
    },
  },
}
</script>

<style lang="scss">
.vehicle-list {
  .click {
    text-decoration: none;
    color: #71A723;
    font-weight: bold;
  }
}
</style>
